import { useAssertedActiveTabId } from 'hooks/use-asserted-active-tab-id';
import { useAppSelector } from 'hooks/useAppSelector';
import { useThunkDispatch } from 'hooks/useThunkDispatch';
import { useUserNotification } from 'hooks/useUserNotification';
import { useEffect, useState } from 'react';
import {
  selectCustomProductLayoutSections,
  selectDefaultCustomProductLayoutSections,
} from 'routes/essential-workspace/state/custom-layout.selectors';
import { CustomFieldPreferences, FieldId, LayoutSection } from 'routes/essential-workspace/state/model';
import { Width } from 'state/product-description/types';
import { updateUserPreferenceProductLayoutThunk } from 'state/userPreferences/userPreferences.thunks';
import { selectEssentialWorkspaceTabProductVariation } from 'state/workspaces/essential/selectors';

type UseLayoutPreferencesParams = {
  currentSectionId: string;
  setCurrentSection: (sectionId: string) => void;
  closePanel: () => void;
};

export const useLayoutPreferences = ({
  currentSectionId,
  setCurrentSection,
  closePanel,
}: UseLayoutPreferencesParams) => {
  const activeTabId = useAssertedActiveTabId();
  const dispatch = useThunkDispatch();
  const notification = useUserNotification();
  const sectionsFromStore = useAppSelector(selectCustomProductLayoutSections);
  const defaultSectionsFromStore = useAppSelector(selectDefaultCustomProductLayoutSections);
  const storedProductVariation = useAppSelector(state =>
    selectEssentialWorkspaceTabProductVariation(state, activeTabId),
  );

  const [draftSections, setDraftSections] = useState<LayoutSection[]>(sectionsFromStore);

  const currentSection = draftSections.find(s => s.sectionId === currentSectionId);

  // const lockedFields = currentSection?.fields.filter(f => !f.isOrderable) ?? [];
  const visibleFields = currentSection?.fields.filter(f => f.isOrderable && f.isVisible) ?? [];
  const additionalConfigurationFields = currentSection?.fields.filter(f => f.isOrderable && !f.isVisible) ?? [];

  const [isSaving, setIsSaving] = useState(false);

  const showSuccessNotification = (
    contentId: 'toast.customProductLayout.content' | 'toast.customProductLayout.reset.content',
  ) => {
    notification.success({
      title: { id: 'toast.customProductLayout.title' },
      content: [
        {
          id: contentId,
        },
      ],
    });
  };

  useEffect(() => {
    setDraftSections(sectionsFromStore);
    const hasCurrentSection = sectionsFromStore.some(section => section.sectionId === currentSectionId);

    if (!hasCurrentSection) {
      setCurrentSection(sectionsFromStore[0]?.sectionId ?? '');
    }
  }, [currentSectionId, sectionsFromStore, setCurrentSection]);

  const togglePinForVisibleField = (fieldId: FieldId) => {
    setDraftSections(prevSections =>
      prevSections.map(section => ({
        ...section,
        fields: section.fields.map(field => {
          if (field.fieldId !== fieldId || !field.isOrderable) {
            return field;
          }
          return { ...field, isPinned: !field.isPinned };
        }),
      })),
    );
  };

  const saveProductLayout = async ({
    sections = draftSections,
    resetToDefault = false,
  }: {
    sections?: LayoutSection[];
    resetToDefault?: boolean;
  } = {}) => {
    if (!storedProductVariation) {
      return;
    }

    setIsSaving(true);
    try {
      const allFields = sections.flatMap(s => s.fields);
      await dispatch(
        updateUserPreferenceProductLayoutThunk(
          storedProductVariation,
          resetToDefault
            ? null
            : {
                visibleFields: allFields.filter(f => f.isVisible && f.isOrderable).map(f => f.fieldId),
                additionalFields: allFields.filter(f => !f.isVisible && f.isOrderable).map(f => f.fieldId),
                pinnedFields: allFields.filter(f => f.isPinned).map(f => f.fieldId),
                fieldOverrides: Object.fromEntries(
                  allFields
                    .filter(field => field.width !== field.initialWidth)
                    .map(field => [field.fieldId, field.width]),
                ),
              },
        ),
      );
    } finally {
      setIsSaving(false);
    }
  };

  const updateCustomFields = ({ visibleFields, additionalConfigurationFields }: CustomFieldPreferences) => {
    const newDraftSections = draftSections.map(section => {
      if (section.sectionId !== currentSectionId) {
        return section;
      }

      return {
        ...section,
        fields: [
          ...visibleFields.map(f => ({
            ...f,
            isVisible: true,
          })),
          ...additionalConfigurationFields.map(f => ({
            ...f,
            isVisible: false,
          })),
        ],
      };
    });
    setDraftSections(newDraftSections);

    void saveProductLayout({ sections: newDraftSections });
  };

  const resetToDefaultLayout = async () => {
    setDraftSections(defaultSectionsFromStore);
    await saveProductLayout({ sections: defaultSectionsFromStore, resetToDefault: true });
    showSuccessNotification('toast.customProductLayout.reset.content');
  };

  const submitLayoutChanges = async () => {
    showSuccessNotification('toast.customProductLayout.content');
    closePanel();
  };

  const updateFieldWidth = (fieldId: FieldId, width: Width) => {
    const newDraftSections = draftSections.map(section => ({
      ...section,
      fields: section.fields.map(field => (field.fieldId === fieldId ? { ...field, width } : field)),
    }));

    setDraftSections(newDraftSections);
    void saveProductLayout({ sections: newDraftSections });
  };

  return {
    sections: draftSections,
    visibleFields,
    additionalConfigurationFields,
    togglePinForVisibleField,
    resetToDefaultLayout,
    isSaving,
    submitLayoutChanges,
    updateCustomFields,
    updateFieldWidth,
  };
};
